import React, { Component } from 'react';
import gql from 'graphql-tag';
import { Elastic, TimelineLite } from 'gsap/all';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import { selectTourActive } from 'components/IntroTour/selectors';
import { selectOnboardActive } from 'components/OnboardTour/selectors';
import WScorePopupDisplay from 'components/WScorePopupDisplay';
import WScoreWheel from 'components/WScoreWheel';
import { isProdEnv } from 'utils/constants';
import { errorShape } from 'utils/shapes';
import withMountCondition from 'utils/withMountCondition';
import { setWScoreWheelActive } from './actions';
import withDailyBonusSounds from './sound';
import wheelCenter from './assets/Button-Spin.png';
import './WScoreWheelPopup.css';

// stagger reset popup within a 10 second interval so that all users don't bombard server with gets in the same ms
const RESET_STAGGER_TIMEFRAME = 1000 * 10;

class WScoreWheelPopup extends Component {
  constructor(props) {
    super(props);

    this.START_DELAY_MS = 300;
    this.RESULT_DISPLAY_MS = 4000;

    this.handleStateChange = this.handleStateChange.bind(this);

    this.unsubscribeFromNewData = () => {};

    this.state = {
      stopData: { index: null, multiplier: null },
      winAmount: null,
      pending: false,
      fadeIn: false,
      wheelLoading: true,
      spinComplete: true,

      beginSpin: false, //todo: this is the lifted state

      hidableClass: 'wscore-popup-viewport' // dev only to hide so it's not annoying
    };

    this.beginSpinAnimation = this.beginSpinAnimation.bind(this);

    this.timeline = new TimelineLite({ paused: true });
    this.background = null;
    this.wheel = null;
    this.title = null;
    this.multiplierImg = null;
    this.text = null;
    this.button = null;
    this.disclaimerInfo = null;
    this.goodLuck = null;

    this.resultTimeline = new TimelineLite({ paused: true });
    this.confetti = null;
  }

  componentDidMount() {
    this.unsubscribeFromNewData = this.props.subscribeToNewData();
  }

  shouldShowWheel(props) {
    return (
      !props.wedgeData.loading &&
      props.wedgeData.wscoreWheelAmounts &&
      props.wedgeData.wscoreWheelAmounts.spinAvailable
    );
  }

  componentWillUnmount() {
    this.unsubscribeFromNewData();
    this.unsubscribeFromNewData = () => {};
  }

  handleStateChange(prevState, state) {
    if (state === 'RESULTS') {
      this.setState({
        winAmount:
          this.state.wscoreWheelAmounts.wheelData[this.state.stopData.index]
            .amount
      });
      this.fadeInConfetti();

      this.confirmStop();
    } else if (prevState === 'RESULTS' && state === 'IDLE') {
      this.setState({ spinComplete: true });
    }
  }

  beginSpinAnimation() {
    this.setState({ beginSpin: true });
    this.spin();
    this.getStop();
  }

  confirmStop() {
    // tell the server we stopped so it can finalize the spin and add wscore.
    this.props.confirmStopWedge().then(resp => {
      if (resp && resp.data && resp.data.confirmWScoreWheelWin) {
        const {
          stopData: { index, multiplier } = {},
          wscoreWheelAmounts: { wheelData = [] } = {}
        } = this.state;
        const { amount } = wheelData[index] || {};

        trackUserEvent({
          activityType: SELECTION_EVENT,
          eventName: 'daily-bonus-success',
          eventValue: amount * multiplier,
          eventParams: `bonusValue=${amount}&bonusMultiplier=${multiplier}`,
          initiator: 'daily-bonus-spinner'
        });
      } else {
        // failure confirming spin result
        console.error('spin result confirmation failure');
      }
    });
  }

  getStop() {
    this.props.selectStopWedge().then(resp => {
      if (resp && resp.data) {
        if (
          this.state.wscoreWheelAmounts.multiplier !==
          resp.data.computeWScoreWheelAmountIndex.multiplier
        ) {
          // this should rarely happen, guest would need to spin after a reset but before subscription comes in
          this.props.wedgeData.refetch().then(() => {
            this.setState({
              stopData: resp.data.computeWScoreWheelAmountIndex
            });
          });
        }

        this.setState({ stopData: resp.data.computeWScoreWheelAmountIndex });
      } else {
        // problem getting stop wedge
      }
    });
  }

  playBonusVisible() {
    if (!this.props.tourActive && !this.props.onboardActive) {
      this.props.playBonusVisible();
    }
  }

  playBonusPop() {
    if (!this.props.tourActive && !this.props.onboardActive) {
      this.props.playBonusPop();
    }
  }

  playFadeIn() {
    this.timeline.kill().clear().pause(0);
    this.timeline
      .to(this.background, 0.75, { opacity: 1, top: '1.78571rem' })
      .call(this.playBonusVisible, null, this, '-=0.55')
      .to(this.wheel, 1, { scale: 1, ease: Elastic.easeOut.config(1, 1) }, '.3')
      .to(
        this.title,
        1,
        { scale: 1, ease: Elastic.easeOut.config(1, 1) },
        '.55'
      )
      .to(
        this.disclaimerInfo,
        1,
        { scale: 1, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
        '.85'
      )
      .call(this.playBonusPop, null, this, '-=0.605')
      .to(
        this.multiplierImg,
        1,
        { scale: 1, ease: Elastic.easeOut.config(1, 1) },
        '.7'
      )
      .call(this.playBonusPop, null, this, '-=0.605')
      .to(
        this.text,
        1,
        { scale: 1, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
        '.85'
      )
      .call(this.playBonusPop, null, this, '-=0.605')
      .to(
        this.button,
        1,
        { scale: 1, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
        '1'
      )
      .call(this.playBonusPop, null, this, '-=0.605')
      .play();
  }

  playFadeOut() {
    this.timeline.kill().clear().pause(0);
    this.timeline
      .to(
        this.button,
        1,
        {
          scale: 0,
          ease: Elastic.easeIn.config(1, 1)
        },
        '1'
      )
      .to(
        this.multiplierImg,
        1,
        { scale: 0, ease: Elastic.easeIn.config(1, 1) },
        '1.3'
      )
      .to(this.text, 1, { scale: 0, ease: Elastic.easeIn.config(1, 1) }, '1.3')
      .to(
        this.title,
        1,
        { scale: 0, ease: Elastic.easeIn.config(1, 1) },
        '1.55'
      )
      .to(this.wheel, 1, { scale: 0, ease: Elastic.easeIn.config(1, 1) }, '1.7')
      .to(
        this.background,
        1,
        {
          opacity: 0,
          top: '7rem',
          onComplete: () => {
            // tell wheel it's done so it can go back to idle and move us forward
            // write any queued props data to state
            this.setState(
              {
                fadeIn: false,
                winAmount: null,
                stopData: { index: null, multiplier: null },
                wscoreWheelAmounts: this.props.wedgeData.wscoreWheelAmounts,
                spinComplete: true // set here also in case fadeout came from subscription only
              },
              () => {
                this.props.setWScoreWheelActive(false);
              }
            );
            this.resetConfetti();
            // check for updated props data
            this.props.wedgeData.refetch();
          }
        },
        '2'
      )
      .play();
  }

  //make everything else animate out and animate the new view
  fadeInConfetti() {
    this.timeline.kill().clear().pause(0);
    this.timeline
      .to(
        this.disclaimerInfo,
        1,
        { scale: 0, opacity: 0, ease: Elastic.easeIn.config(1, 1) },
        '.2'
      )
      .to(this.text, 1, { scale: 0, ease: Elastic.easeIn.config(1, 1) }, '.2')
      .to(this.title, 1, { scale: 0, ease: Elastic.easeIn.config(1, 1) }, '.2')
      .to(
        this.multiplierImg,
        1,
        { scale: 0, ease: Elastic.easeIn.config(1, 1) },
        '.2'
      )
      .play();
  }

  resetConfetti() {
    this.resultTimeline.restart().kill().clear().pause(0);
  }

  componentDidUpdate(prevProps, prevState) {
    // spin and results in progress
    if (!this.state.spinComplete) {
      // if show and was hidden or results
      if (
        this.shouldShowWheel(this.props) &&
        !this.shouldShowWheel(prevProps)
      ) {
        // do nothing to queue changes until fade in
      } else if (
        !this.shouldShowWheel(this.props) &&
        this.shouldShowWheel(prevProps)
      ) {
        // if hide and was shown
        // fade out
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.playFadeOut();
        }, this.RESULT_DISPLAY_MS);
      } else if (
        this.state.wscoreWheelAmounts !==
        this.props.wedgeData.wscoreWheelAmounts
      ) {
        // otherwise values changed update striaght away
        this.setState({
          wscoreWheelAmounts: this.props.wedgeData.wscoreWheelAmounts
        });
      }
    } else if (this.shouldShowWheel(this.props)) {
      // we were idle, so fade in and apply right away
      // fade in
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.setState({
          pending: false,
          fadeIn: true,
          stopData: { index: null, multiplier: null }
        });
        this.playFadeIn();
      }, this.START_DELAY_MS);
      this.setState(
        {
          wscoreWheelAmounts: this.props.wedgeData.wscoreWheelAmounts,
          pending: true,
          spinComplete: false
        },
        () => {
          this.props.setWScoreWheelActive(true);
        }
      );
    }
  }

  shouldRender() {
    return this.state.fadeIn || !this.state.spinComplete;
  }

  renderPopup() {
    if (this.shouldRender())
      return (
        <div className="wscore-popup">
          <div
            className="wscore-popup-content"
            ref={div => (this.background = div)}
          >
            <div className="confetti-box" ref={ref => (this.confetti = ref)}>
              <div className="confetti" />
              <div className="offset-confetti" />
            </div>
            <WScorePopupDisplay
              startSpin={() => {
                this.spin();
                this.getStop();
              }}
              beginSpin={this.state.beginSpin}
              multiplier={this.state.wscoreWheelAmounts.multiplier}
              winAmount={this.state.winAmount}
              setTitle={ref => (this.title = ref)}
              setMultiplier={ref => (this.multiplierImg = ref)}
              setText={ref => (this.text = ref)}
              setButtonArea={ref => (this.button = ref)}
              setDisclaimerInfo={ref => (this.disclaimerInfo = ref)}
              setGoodLuck={ref => (this.goodLuck = ref)}
            />
            <div className="body-right">
              <WScoreWheel
                loadingCompleteCallback={() => {
                  this.setState({ wheelLoading: false });
                }}
                registerSpinCallback={spinCallback => {
                  this.spin = spinCallback;
                }}
                beginSpinAnimation={this.beginSpinAnimation}
                stateChangeHandler={this.handleStateChange}
                wedgeData={this.state.wscoreWheelAmounts.wheelData}
                stopIndex={this.state.stopData.index}
                multiplier={this.state.wscoreWheelAmounts.multiplier}
                refSetter={ref => (this.wheel = ref)}
              />
              <button
                type="button"
                className="daily-bonus-pop-up-spin-button"
                onClick={this.beginSpinAnimation}
              >
                <img src={wheelCenter} alt="Start spin" />
              </button>
            </div>
            <div className="wscore-wheel-mobile-footer"></div>
          </div>
        </div>
      );
    else return null;
  }

  render() {
    return (
      <div
        className={
          this.state.hidableClass +
          (!this.props.tourActive &&
          !this.props.onboardActive &&
          !this.state.wheelLoading &&
          this.state.fadeIn
            ? ' fade-in'
            : '') +
          (this.state.pending ? ' pending' : '')
        }
      >
        {this.renderPopup()}
        {!isProdEnv() && (
          <button
            type="button"
            onClick={() => {
              this.setState(
                { hidableClass: this.state.hidableClass + ' hide' },
                () => {
                  this.props.setWScoreWheelActive(false);
                }
              );
            }}
          >
            DEV MODE - HIDE
          </button>
        )}
      </div>
    );
  }
}

const QUERY_WSCORE_WHEEL_DATA = gql`
  query MyWScoreWheelAmounts {
    wscoreWheelAmounts {
      wheelData {
        weight
        amount
      }
      multiplier
      spinAvailable
    }
  }
`;

const WSCORE_WHEEL_DATA_OPTIONS = {
  name: 'wedgeData',
  options: {
    fetchPolicy: 'network-only'
  }
};

const MUTATION_WSCORE_WHEEL_INDEX = gql`
  mutation MyComputeWScoreWheelAmountIndex {
    computeWScoreWheelAmountIndex {
      index
      multiplier
    }
  }
`;

const WSCORE_WHEEL_INDEX_OPTIONS = {
  props({ mutate }) {
    return {
      selectStopWedge: () => {
        return mutate();
      }
    };
  }
};

const MUTATION_CONFIRM_WSCORE_WHEEL_WIN = gql`
  mutation MyConfirmWScoreWheelWin {
    confirmWScoreWheelWin
  }
`;

const CONFIRM_WSCORE_WHEEL_WIN_OPTIONS = {
  props({ mutate }) {
    return {
      confirmStopWedge: () => {
        return mutate();
      }
    };
  }
};

const SUBSCRIPTION_WSCORE_WHEEL_AVAILABLE = gql`
  subscription MyWScoreWheelAvailability {
    wscoreWheelAvailability {
      spinAvailable
    }
  }
`;

const withWScoreWheelAvailable = graphql(QUERY_WSCORE_WHEEL_DATA, {
  options: { fetchPolicy: 'cache-and-network' },
  props: ({ ownProps, data }) => {
    return {
      data,
      subscribeToNewData: () => {
        return data.subscribeToMore({
          document: SUBSCRIPTION_WSCORE_WHEEL_AVAILABLE,
          updateQuery: (prev, { subscriptionData }) => {
            const isSpinAvailable = subscriptionData.data.spinAvailable;
            if (isSpinAvailable !== undefined) {
              if (isSpinAvailable) {
                setTimeout(
                  data.refetch,
                  Math.random() * RESET_STAGGER_TIMEFRAME
                );
              } else {
                return Object.assign({}, prev, {
                  wscoreWheelAmounts: Object.assign(
                    {},
                    prev.wscoreWheelAmounts,
                    {
                      spinAvailable: isSpinAvailable
                    }
                  )
                });
              }
            }
            return null;
          }
        });
      }
    };
  }
});

const mapDispatchToProps = dispatch => ({
  setWScoreWheelActive: active => dispatch(setWScoreWheelActive(active))
});

const mapStateToProps = createStructuredSelector({
  tourActive: selectTourActive(),
  onboardActive: selectOnboardActive()
});

WScoreWheelPopup.propTypes = {
  wedgeData: PropTypes.shape({
    wscoreWheelAmounts: PropTypes.shape({
      wheelData: PropTypes.arrayOf(
        PropTypes.shape({
          weight: PropTypes.number,
          amount: PropTypes.number
        })
      ),

      multiplier: PropTypes.number,
      spinAvailable: PropTypes.bool
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  tourActive: PropTypes.bool,
  onboardActive: PropTypes.bool
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

function shouldMountWScoreWheel({ location }) {
  return (
    location.pathname === '/' ||
    location.pathname === '/slots' ||
    location.pathname.startsWith('/game/')
  );
}

export default compose(
  withMountCondition(shouldMountWScoreWheel),
  graphql(QUERY_WSCORE_WHEEL_DATA, WSCORE_WHEEL_DATA_OPTIONS),
  graphql(MUTATION_WSCORE_WHEEL_INDEX, WSCORE_WHEEL_INDEX_OPTIONS),
  graphql(MUTATION_CONFIRM_WSCORE_WHEEL_WIN, CONFIRM_WSCORE_WHEEL_WIN_OPTIONS),
  withWScoreWheelAvailable,
  withDailyBonusSounds,
  withConnect
)(WScoreWheelPopup);
